import { Classification, IncidentCategorySummary, IncidentEventDetailsViewModel, IncidentEventSummary, IncidentTypeSummary } from '../../boxwood-api';
import { IncidentCategory, IncidentEvent, IncidentSettings, IncidentType, MessageSettings } from '../../hickory-api';
import { FixDatePipe } from '../pipes/fix-date/fix-date.pipe';

export class IncidentModelHelper {

  static convertClassification(boxwood?: Classification): IncidentType.ClassificationEnum {
    if (boxwood?.name?.toLowerCase() === IncidentType.ClassificationEnum.Emergency.toLowerCase()) return IncidentType.ClassificationEnum.Emergency;
    if (boxwood?.name?.toLowerCase() === IncidentType.ClassificationEnum.Operations.toLowerCase()) return IncidentType.ClassificationEnum.Operations;
    return Object.values(IncidentType.ClassificationEnum).find(hk => hk.toString().toLowerCase() === boxwood?.name?.toLowerCase());
  }

  static convertIncidentType(boxwood?: IncidentTypeSummary, tamarack: IncidentType = { }): IncidentType {
    if (!boxwood) return undefined;
    Object.assign(tamarack, boxwood, { classification: this.convertClassification(boxwood.classification) });
    return tamarack;
  }

  static convertIncidentCategory(boxwood?: IncidentCategorySummary, tamarack: IncidentCategory = { }): IncidentCategory {
    if (!boxwood) return undefined;
    Object.assign(tamarack, boxwood, { classification: this.convertClassification(boxwood.classification) });
    return tamarack;
  }

  static convertIncidentEvent(boxwood?: IncidentEventSummary, tamarack: IncidentEvent = { }): IncidentEvent {
    if (!boxwood) return undefined;
    Object.assign(tamarack, boxwood, {
      incidentType: this.convertIncidentType(boxwood.incidentType),
      IncidentCategory: this.convertIncidentCategory(boxwood.incidentCategory)
    });
    return tamarack;
  }

  static convertIncidentEventDetails(boxwood?: IncidentEventDetailsViewModel, tamarack: IncidentEvent = { }): IncidentEvent {
    if (!boxwood) return undefined;
    Object.assign(tamarack, boxwood, {
      incidentType: this.convertIncidentType(boxwood.incidentType),
      IncidentCategory: this.convertIncidentCategory(boxwood.incidentCategory)
    });
    return tamarack;
  }

  static isIncidentSettings(obj: any): boolean {
    return obj['priority']!;
  }

  static isIncidentEvent(obj: any): boolean {
    return obj['incidentType']!;
  }

  static getClassification(incident: IncidentEvent): string {
    return incident?.incidentType?.classification?.toString()?.toLowerCase();
  }

  static hasIncident(message?: MessageSettings): boolean {
    return message && (message.incident || message.incidentEvent) ? true : false;
  }

  static getIncidentNameFromMessage(message?: MessageSettings): string {
    return message ? this.getIncidentName(message.incident, message.incidentEvent) : null;
  }

  static getIncidentName(incidentSettings?: IncidentSettings, incidentEvent?: IncidentEvent): string {
    if (incidentSettings) {
      return incidentSettings.name;
    } else if (incidentEvent) {
      if (incidentEvent.name) {
        return incidentEvent.name;
      } else if (incidentEvent.incidentType && incidentEvent.incidentType.name) {
        // fall back to IncidentType.name if no name on the event
        return incidentEvent.incidentType.name;
      }
    }

    return null;
  }

  static EVENT_EMERGENCY_ICON = 'assignment_late';
  static EVENT_OPERATIONS_ICON = 'assignment';
  static getEventIconName(event: IncidentEvent): string {
    const classification = IncidentModelHelper.getClassification(event);
    return classification === 'operations' ? this.EVENT_OPERATIONS_ICON : this.EVENT_EMERGENCY_ICON;
  }

  static EVENT_EMERGENCY_COLOR = '#c72127';
  static EVENT_OPERATIONS_COLOR = '#4a91d6';
  static getEventIconColor(event: IncidentEvent): string {
    const classification = IncidentModelHelper.getClassification(event);
    return classification === 'operations' ? this.EVENT_OPERATIONS_COLOR : this.EVENT_EMERGENCY_COLOR;
  }

  static getDateCreatedDetail(event?: IncidentEventDetailsViewModel): string {
    if (!event || !event.created || !event.created.dateTimeUtc) return null;
    const dateCreated = new FixDatePipe().transform(event.created.dateTimeUtc);
    return dateCreated ? dateCreated.toLocaleString() : null;
  }

  static getCreatedByDetail(event?: IncidentEventDetailsViewModel): string {
    if (!event || !event.created) return null;
    return event.created.by;
  }

  static getCustomLocationDetail(event?: IncidentEventDetailsViewModel): string {
    if (!event || !event.customLocation || !event.customLocation.latitude || !event.customLocation.longitude) return null;
    const latString = 'lat: ' + event.customLocation.latitude.toFixed(5);
    const lngString = ' lng: ' + event.customLocation.longitude.toFixed(5);
    return latString + lngString;
  }

  static getFacilitiesDetail(event?: IncidentEventDetailsViewModel): string {
    if (!event || !event.facilities) return null;
    return event.facilities.map(facility => facility.name).join(', ');
  }

}
